/**
 * Central i18n configuration for the Word add-in.
 *
 * Kept structurally identical to the dashboard's config so the two clients
 * share the same language codes, namespace names, and key hierarchy.
 */

export type LanguageCode = 'en' | 'he';

export interface LanguageMeta {
  code: LanguageCode;
  label: string;
  dir: 'ltr' | 'rtl';
}

export const SUPPORTED_LANGUAGES: LanguageMeta[] = [
  { code: 'en', label: 'English', dir: 'ltr' },
  { code: 'he', label: 'עברית', dir: 'rtl' },
];

export const DEFAULT_LANGUAGE: LanguageCode = 'he';

export const NAMESPACES = [
  'common',
  'auth',
  'taskpane',
  'rephrasing',
  'mcc',
  'ask',
] as const;

export type Namespace = (typeof NAMESPACES)[number];

export const DEFAULT_NAMESPACE: Namespace = 'common';

export const LANGUAGE_STORAGE_KEY = 'ui_language';

const SUPPORTED_CODES = new Set<string>(SUPPORTED_LANGUAGES.map((l) => l.code));

export function normalizeLanguage(value: string | null | undefined): LanguageCode {
  if (!value) return DEFAULT_LANGUAGE;
  const base = value.toLowerCase().split('-')[0];
  return SUPPORTED_CODES.has(base) ? (base as LanguageCode) : DEFAULT_LANGUAGE;
}

export function directionForLanguage(code: string | null | undefined): 'ltr' | 'rtl' {
  const normalized = normalizeLanguage(code);
  return SUPPORTED_LANGUAGES.find((l) => l.code === normalized)?.dir ?? 'ltr';
}
